import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { affirmationPlanAdminService } from '../services/apiService';

const AdminAffirmationPlanDetail: React.FC = () => {
  const { token } = useAuth();
  const { planId } = useParams<{ planId: string }>();

  const [plan, setPlan] = useState<any>(null);
  const [startDate, setStartDate] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [expandedDays, setExpandedDays] = useState<Set<number>>(new Set());

  const load = useCallback(async () => {
    if (!token || !planId) return;
    try {
      const res = await affirmationPlanAdminService.get(token, planId);
      setPlan(res.data);
      if (res.data?.startDate) setStartDate(String(res.data.startDate).slice(0, 10));
    } catch (e) {
      setMsg({ type: 'err', text: e instanceof Error ? e.message : 'Plan yüklenemedi' });
    }
  }, [token, planId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (plan?.status !== 'Generating') return;

    const poll = window.setInterval(() => {
      void load();
    }, 10_000);

    return () => window.clearInterval(poll);
  }, [plan?.status, load]);

  const post = async (path: string, body?: unknown) => {
    if (!token || !planId) return;
    setBusy(true);
    setMsg(null);
    try {
      await affirmationPlanAdminService.post(token, planId, path, body);
      setMsg({
        type: 'ok',
        text:
          path === '/regenerate'
            ? 'Claude içerik üretimini arka planda başlattı. Bu sayfa tamamlanınca otomatik güncellenecek.'
            : 'İşlem başarılı',
      });
      await load();
    } catch (e) {
      setMsg({ type: 'err', text: e instanceof Error ? e.message : 'İşlem başarısız' });
    } finally {
      setBusy(false);
    }
  };

  const saveDay = async (dayNumber: number, day: any) => {
    if (!token || !planId) return;
    setBusy(true);
    setMsg(null);
    try {
      await affirmationPlanAdminService.updateDay(token, planId, dayNumber, {
        title: day.title,
        affirmationText: day.affirmationText,
        affirmationPurpose: day.affirmationPurpose,
        recommendedRepeatCount: day.recommendedRepeatCount,
        affirmationInstruction: day.affirmationInstruction,
        miniExerciseTitle: day.miniExerciseTitle,
        miniExerciseQuestion: day.miniExerciseQuestion,
        miniExerciseDescription: day.miniExerciseDescription,
        miniExerciseHint: day.miniExerciseHint,
        minimumCharacterCount: day.minimumCharacterCount,
        moodQuestions: (day.moodQuestions || []).map((q: any) => ({
          id: q.id,
          order: q.order,
          question: q.question,
          dimension: q.dimension,
          options: (q.options || []).map((o: any) => ({
            id: o.id,
            order: o.order,
            text: o.text,
            score: o.score,
          })),
        })),
        changeReason: 'Admin panel düzenlemesi',
      });
      setMsg({ type: 'ok', text: `Gün ${dayNumber} kaydedildi` });
      await load();
    } catch (e) {
      setMsg({ type: 'err', text: e instanceof Error ? e.message : 'Kayıt başarısız' });
    } finally {
      setBusy(false);
    }
  };

  const updateDayField = (day: any, field: string, value: unknown) => {
    day[field] = value;
    setPlan({ ...plan });
  };

  const updateMoodField = (day: any, questionIndex: number, field: string, value: unknown) => {
    day.moodQuestions[questionIndex][field] = value;
    setPlan({ ...plan });
  };

  const updateMoodOption = (day: any, questionIndex: number, optionIndex: number, field: string, value: unknown) => {
    day.moodQuestions[questionIndex].options[optionIndex][field] = value;
    setPlan({ ...plan });
  };

  const toggleDay = (dayNumber: number) => {
    setExpandedDays((current) => {
      const next = new Set(current);
      if (next.has(dayNumber)) next.delete(dayNumber);
      else next.add(dayNumber);
      return next;
    });
  };

  const toggleAllDays = () => {
    setExpandedDays((current) =>
      current.size === (plan.days || []).length
        ? new Set()
        : new Set((plan.days || []).map((day: any) => day.dayNumber))
    );
  };

  if (!plan) {
    return <div className="p-6 text-gray-600">Yükleniyor…</div>;
  }

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8">
      <Link to="/admin/affirmation-plans" className="text-sm font-medium text-purple-600 hover:underline">
        ← Listeye dön
      </Link>

      <h1 className="text-2xl font-bold text-gray-900">{plan.planTitle || plan.topic}</h1>

      <div className="space-y-1 rounded-xl border border-gray-100 bg-white p-4 text-sm shadow-sm">
        <div>
          <b>Kullanıcı:</b> {plan.userEmail}
        </div>
        <div>
          <b>Konu:</b> {plan.topic}
        </div>
        <div>
          <b>Hedef:</b> {plan.goal}
        </div>
        {plan.importanceDescription && (
          <div>
            <b>Neden önemli:</b> {plan.importanceDescription}
          </div>
        )}
        {plan.additionalDescription && (
          <div>
            <b>Ek bilgi:</b> {plan.additionalDescription}
          </div>
        )}
        <div>
          <b>Durum:</b> {plan.status}
        </div>
        <div>
          <b>Deneme:</b> {plan.isTrial ? 'Evet' : 'Hayır'}
        </div>
        {plan.generationError && <div className="text-red-600">{plan.generationError}</div>}
      </div>

      <div className="rounded-xl border border-purple-100 bg-purple-50 p-4 shadow-sm">
        <h2 className="text-base font-semibold text-purple-950">Claude’un plan özeti</h2>
        {plan.planSummary ? (
          <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-purple-950">{plan.planSummary}</p>
        ) : (
          <p className="mt-2 text-sm text-purple-800">Henüz plan özeti üretilmemiş.</p>
        )}
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-purple-800">
          <span>Üretim denemesi: {plan.generationAttemptCount ?? 0}</span>
          <span>Üretim tarihi: {plan.generatedAt ? new Date(plan.generatedAt).toLocaleString('tr-TR') : '—'}</span>
          <span>İçerik günü: {(plan.days || []).length}/21</span>
        </div>
      </div>

      {plan.status === 'Generating' && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          Claude 21 günlük içeriği hazırlıyor. Sayfa her 10 saniyede bir otomatik yenilenir; bu işlem birkaç dakika sürebilir.
        </div>
      )}

      {msg && (
        <div
          className={`rounded-lg px-4 py-3 text-sm ${
            msg.type === 'ok'
              ? 'border border-green-200 bg-green-50 text-green-800'
              : 'border border-amber-200 bg-amber-50 text-amber-800'
          }`}
        >
          {msg.text}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
          disabled={busy}
        />
        <button
          type="button"
          disabled={busy}
          className="rounded-lg bg-green-600 px-4 py-2 text-sm text-white hover:bg-green-700 disabled:opacity-50"
          onClick={() => post('/approve', { startDate: startDate || null })}
        >
          Onayla
        </button>
        <button
          type="button"
          disabled={busy || !startDate}
          className="rounded-lg bg-slate-700 px-4 py-2 text-sm text-white hover:bg-slate-800 disabled:opacity-50"
          onClick={() => post('/schedule', { startDate })}
        >
          Başlangıç belirle
        </button>
        <button
          type="button"
          disabled={busy}
          className="rounded-lg bg-purple-600 px-4 py-2 text-sm text-white hover:bg-purple-700 disabled:opacity-50"
          onClick={() => post('/regenerate')}
        >
          Claude ile yeniden üret
        </button>
        <button
          type="button"
          disabled={busy}
          className="rounded-lg bg-amber-500 px-4 py-2 text-sm text-white hover:bg-amber-600 disabled:opacity-50"
          onClick={() => post('/request-revision', { description: 'İçerik revizyonu istendi' })}
        >
          Düzenleme iste
        </button>
      </div>

      <div className="flex gap-2">
        <input
          className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm"
          placeholder="Red nedeni"
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          disabled={busy}
        />
        <button
          type="button"
          disabled={busy || !rejectReason.trim()}
          className="rounded-lg bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 disabled:opacity-50"
          onClick={() => post('/reject', { reason: rejectReason })}
        >
          Reddet
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Claude’un 21 günlük içeriği</h2>
          <p className="mt-1 text-sm text-gray-600">Her günü açıp tüm metinleri, soruları ve seçenekleri manuel olarak düzenleyebilirsin.</p>
        </div>
        {(plan.days || []).length > 0 && (
          <button
            type="button"
            onClick={toggleAllDays}
            className="rounded-lg border border-purple-200 bg-white px-3 py-2 text-sm font-medium text-purple-700 hover:bg-purple-50"
          >
            {expandedDays.size === plan.days.length ? 'Tümünü kapat' : 'Tümünü aç'}
          </button>
        )}
      </div>

      {(plan.days || []).length === 0 ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
          Bu plan için henüz gün içeriği bulunmuyor. “Claude ile yeniden üret” ile 21 günlük içerik oluşturabilirsin.
        </div>
      ) : (
        (plan.days || []).map((day: any, idx: number) => {
          const expanded = expandedDays.has(day.dayNumber);
          return (
            <section key={day.id || idx} className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <button
                type="button"
                onClick={() => toggleDay(day.dayNumber)}
                className="flex w-full items-start justify-between gap-4 p-4 text-left hover:bg-gray-50"
                aria-expanded={expanded}
              >
                <span>
                  <span className="block font-semibold text-gray-900">Gün {day.dayNumber}: {day.title}</span>
                  <span className="mt-1 line-clamp-2 block text-sm text-gray-600">{day.affirmationText}</span>
                </span>
                <span className="shrink-0 text-sm font-medium text-purple-700">{expanded ? 'Kapat ↑' : 'İncele / düzenle ↓'}</span>
              </button>

              {expanded && (
                <div className="space-y-5 border-t border-gray-100 p-4">
                  <div className="flex flex-wrap justify-end gap-3">
                    <button
                      type="button"
                      disabled={busy}
                      className="text-sm text-purple-600 hover:underline disabled:opacity-50"
                      onClick={() => post(`/days/${day.dayNumber}/regenerate`)}
                    >
                      Bu günü Claude ile yeniden üret
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      className="text-sm text-green-700 hover:underline disabled:opacity-50"
                      onClick={() => saveDay(day.dayNumber, day)}
                    >
                      Bu günün değişikliklerini kaydet
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      className="text-sm text-amber-700 hover:underline disabled:opacity-50"
                      onClick={() => post(`/days/${day.dayNumber}/grant-makeup`, { reason: 'Admin telafi' })}
                    >
                      Telafi ver
                    </button>
                  </div>

                  <div className="grid gap-4 lg:grid-cols-2">
                    <Field label="Gün başlığı">
                      <input
                        className="w-full rounded-lg border border-gray-200 p-2 text-sm"
                        value={day.title || ''}
                        onChange={(e) => updateDayField(day, 'title', e.target.value)}
                      />
                    </Field>
                    <Field label="Tekrar sayısı">
                      <input
                        type="number"
                        min="1"
                        className="w-full rounded-lg border border-gray-200 p-2 text-sm"
                        value={day.recommendedRepeatCount ?? 3}
                        onChange={(e) => updateDayField(day, 'recommendedRepeatCount', Number(e.target.value))}
                      />
                    </Field>
                  </div>

                  <Field label="Olumlama metni">
                    <textarea
                      className="w-full rounded-lg border border-gray-200 p-2 text-sm"
                      rows={3}
                      value={day.affirmationText || ''}
                      onChange={(e) => updateDayField(day, 'affirmationText', e.target.value)}
                    />
                  </Field>
                  <Field label="Olumlamanın amacı">
                    <textarea
                      className="w-full rounded-lg border border-gray-200 p-2 text-sm"
                      rows={2}
                      value={day.affirmationPurpose || ''}
                      onChange={(e) => updateDayField(day, 'affirmationPurpose', e.target.value)}
                    />
                  </Field>
                  <Field label="Uygulama talimatı">
                    <textarea
                      className="w-full rounded-lg border border-gray-200 p-2 text-sm"
                      rows={2}
                      value={day.affirmationInstruction || ''}
                      onChange={(e) => updateDayField(day, 'affirmationInstruction', e.target.value)}
                    />
                  </Field>

                  <div className="rounded-lg border border-sky-100 bg-sky-50 p-4">
                    <h3 className="mb-3 font-semibold text-sky-950">Mini çalışma</h3>
                    <div className="space-y-3">
                      <Field label="Başlık">
                        <input
                          className="w-full rounded-lg border border-sky-200 p-2 text-sm"
                          value={day.miniExerciseTitle || ''}
                          onChange={(e) => updateDayField(day, 'miniExerciseTitle', e.target.value)}
                        />
                      </Field>
                      <Field label="Soru">
                        <textarea
                          className="w-full rounded-lg border border-sky-200 p-2 text-sm"
                          rows={3}
                          value={day.miniExerciseQuestion || ''}
                          onChange={(e) => updateDayField(day, 'miniExerciseQuestion', e.target.value)}
                        />
                      </Field>
                      <Field label="Açıklama">
                        <textarea
                          className="w-full rounded-lg border border-sky-200 p-2 text-sm"
                          rows={2}
                          value={day.miniExerciseDescription || ''}
                          onChange={(e) => updateDayField(day, 'miniExerciseDescription', e.target.value)}
                        />
                      </Field>
                      <div className="grid gap-3 lg:grid-cols-2">
                        <Field label="İpucu">
                          <input
                            className="w-full rounded-lg border border-sky-200 p-2 text-sm"
                            value={day.miniExerciseHint || ''}
                            onChange={(e) => updateDayField(day, 'miniExerciseHint', e.target.value)}
                          />
                        </Field>
                        <Field label="Minimum karakter">
                          <input
                            type="number"
                            min="0"
                            className="w-full rounded-lg border border-sky-200 p-2 text-sm"
                            value={day.minimumCharacterCount ?? 50}
                            onChange={(e) => updateDayField(day, 'minimumCharacterCount', Number(e.target.value))}
                          />
                        </Field>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-violet-100 bg-violet-50 p-4">
                    <h3 className="mb-3 font-semibold text-violet-950">Ruh hali soruları ve seçenekleri</h3>
                    <div className="space-y-4">
                      {(day.moodQuestions || []).map((q: any, questionIndex: number) => (
                        <div key={q.id || questionIndex} className="rounded-lg border border-violet-200 bg-white p-3">
                          <div className="grid gap-3 lg:grid-cols-[auto_1fr_1fr]">
                            <Field label="Sıra">
                              <input
                                type="number"
                                min="1"
                                className="w-full rounded-lg border border-gray-200 p-2 text-sm"
                                value={q.order}
                                onChange={(e) => updateMoodField(day, questionIndex, 'order', Number(e.target.value))}
                              />
                            </Field>
                            <Field label={`Soru ${questionIndex + 1}`}>
                              <input
                                className="w-full rounded-lg border border-gray-200 p-2 text-sm"
                                value={q.question || ''}
                                onChange={(e) => updateMoodField(day, questionIndex, 'question', e.target.value)}
                              />
                            </Field>
                            <Field label="Boyut">
                              <input
                                className="w-full rounded-lg border border-gray-200 p-2 text-sm"
                                value={q.dimension || ''}
                                onChange={(e) => updateMoodField(day, questionIndex, 'dimension', e.target.value)}
                              />
                            </Field>
                          </div>
                          <div className="mt-3 grid gap-2 md:grid-cols-2">
                            {(q.options || []).map((option: any, optionIndex: number) => (
                              <div key={option.id || optionIndex} className="flex gap-2">
                                <input
                                  className="min-w-0 flex-1 rounded-lg border border-gray-200 p-2 text-sm"
                                  value={option.text || ''}
                                  onChange={(e) => updateMoodOption(day, questionIndex, optionIndex, 'text', e.target.value)}
                                />
                                <input
                                  type="number"
                                  min="1"
                                  max="4"
                                  aria-label="Puan"
                                  className="w-16 rounded-lg border border-gray-200 p-2 text-sm"
                                  value={option.score}
                                  onChange={(e) => updateMoodOption(day, questionIndex, optionIndex, 'score', Number(e.target.value))}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="button"
                      disabled={busy}
                      className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                      onClick={() => saveDay(day.dayNumber, day)}
                    >
                      Gün {day.dayNumber} değişikliklerini kaydet
                    </button>
                  </div>
                </div>
              )}
            </section>
          );
        })
      )}

      <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
        <h3 className="mb-2 font-semibold text-gray-900">Audit Log</h3>
        <ul className="space-y-1 text-sm text-gray-600">
          {(plan.auditLog || []).map((h: any) => (
            <li key={h.id}>
              {new Date(h.createdAt).toLocaleString('tr-TR')} — {h.action}: {h.description}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block">
    <span className="mb-1 block text-xs font-medium text-gray-600">{label}</span>
    {children}
  </label>
);

export default AdminAffirmationPlanDetail;
